import { FC, useState, useRef, useEffect } from "react";
import {
  Drawer,
  DrawerBody,
  Divider,
  SearchBox,
  DrawerHeader,
} from "@fluentui/react-components";
import {
  Search24Regular,
  LineHorizontal3Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  Dismiss24Regular,
  Open16Regular,
} from "@fluentui/react-icons";
import useMobileHeaderStyles from "./MobileHeader.styles";
import logo from "../../assets/images/mobile header logo.png";
import Typography from "../../components/Typography";
import { customTokens } from "../../theme/theme";
import { Link, useNavigate } from "react-router-dom";
import ROUTES from "../../constants/routes";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  setOpenSearchPage,
  setSearchText,
} from "../../store/slices/GlobalSearchSlice";

interface IHeader {
  triggerSearchApi: any;
}

const Header: FC<IHeader> = ({ triggerSearchApi }) => {
  const styles = useMobileHeaderStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef<HTMLDivElement | null>(null);

  const { searchText } = useSelector((state: RootState) => state.globalSearch);

  // Track expanded state for menu sections
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});

  const toggleExpand = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Close search bar when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowSearch(false);
      }
    }

    if (showSearch) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearch]);

  const handleRedirect = (url: string) => {
    navigate(url);
    setOpen(false);
    dispatch(setOpenSearchPage(false));
    dispatch(setSearchText(""));
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchText.trim().length >= 3) {
      const payload = {
        query: searchText,
        page: 1,
        pageSize: 10,
        sort: "Page",
      };

      triggerSearchApi(payload);
    }
  };

  const handleChange = (newValue: string) => {
    dispatch(setSearchText(newValue));

    if (newValue === "") {
      dispatch(setOpenSearchPage(false));
    }
  };

  return (
    <>
      {/* Header */}
      <header className={styles.header}>
        <img
          src={logo}
          alt="UN Logo"
          className={styles.logo}
          onClick={() => navigate(ROUTES.HOME)}
        />
        <Divider vertical className={styles.divider} />
        <Typography
          variant="caption1"
          color={customTokens.colorNeutralForegroundInverted}
        >
          Climate Data Hub
        </Typography>
        <div className={styles.rightSection}>
          <Search24Regular
            className={styles.icon}
            onClick={() => setShowSearch((prev) => !prev)}
          />
          <LineHorizontal3Regular
            className={styles.icon}
            onClick={() => setOpen(true)}
          />
        </div>
      </header>

      {showSearch && (
        <div className={styles.searchBar} ref={searchRef}>
          <SearchBox
            appearance="underline"
            placeholder="Search in Climate Data Hub"
            className={styles.searchBox}
            value={searchText}
            onChange={(_, data) => handleChange(data.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
      )}

      <Drawer
        open={open}
        position="end"
        onOpenChange={(_, { open }) => setOpen(open)}
        size="full"
        style={{ border: "none" }}
      >
        <DrawerHeader style={{ padding: 0 }}>
          <header className={styles.header}>
            <img src={logo} alt="UN Logo" className={styles.logo} />
            <Divider vertical className={styles.divider} />
            <Typography
              variant="caption1"
              color={customTokens.colorNeutralForegroundInverted}
            >
              Climate Data Hub
            </Typography>
            <div className={styles.rightSection}>
              <Search24Regular
                className={styles.icon}
                onClick={() => {
                  setShowSearch((prev) => !prev);
                  setOpen(false);
                }}
              />
              <Dismiss24Regular
                className={styles.icon}
                onClick={() => setOpen(false)}
              />
            </div>
          </header>
        </DrawerHeader>

        <DrawerBody className={styles.drawerBody}>
          <div
            className={styles.drawerItem}
            onClick={() => handleRedirect(ROUTES.HOME)}
          >
            <span>Home</span>
            <ChevronRight20Regular />
          </div>

          <div
            className={styles.drawerItem}
            onClick={() => toggleExpand("datasets")}
          >
            <span>Datasets</span>
            {expanded.datasets ? (
              <ChevronDown20Regular />
            ) : (
              <ChevronRight20Regular />
            )}
          </div>
          {expanded.datasets && (
            <div className={styles.subMenu}>
              <span
                style={{ padding: "8px 0", cursor: "pointer" }}
                onClick={() =>
                  handleRedirect(`${ROUTES.DATASETS}${ROUTES.FTC_DATASETS}`)
                }
              >
                FTC Support
              </span>
              <span className={styles.disabled} style={{ padding: "8px 0" }}>
                GHG
              </span>
              <span className={styles.disabled} style={{ padding: "8px 0" }}>
                NDC Progress
              </span>
              <span className={styles.disabled} style={{ padding: "8px 0" }}>
                Article 6
              </span>
              <span className={styles.disabled} style={{ padding: "8px 0" }}>
                Mitigation
              </span>
              <span className={styles.disabled} style={{ padding: "8px 0" }}>
                Adaptation and Resilience
              </span>
              <span className={styles.disabled} style={{ padding: "8px 0" }}>
                Non-Party Stakeholders
              </span>
              <span className={styles.disabled} style={{ padding: "8px 0" }}>
                Global Stocktake
              </span>
            </div>
          )}

          <div
            className={styles.drawerItem}
            onClick={() => handleRedirect(ROUTES.COUNTRIES)}
          >
            <span>Countries</span>
            <ChevronRight20Regular />
          </div>

          {/* <div
            className={styles.drawerItem}
            onClick={() => handleRedirect(ROUTES.CLIMATE_ASSISTANT)}
          >
            <span>Climate Assistant</span>
            <ChevronRight20Regular />
          </div> */}

          <div
            className={styles.drawerItem}
            onClick={() => handleRedirect(ROUTES.ABOUT)}
          >
            <span>About</span>
            <ChevronRight20Regular />
          </div>

          <Link
            to={"http://unfccc.int/"}
            className={styles.unfcccLink}
            target="_blank"
          >
            UNFCCC Main page <Open16Regular />
          </Link>
        </DrawerBody>
      </Drawer>
    </>
  );
};

export default Header;
